import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Pill } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import { Handshake, Plus, X, Save, Search, RefreshCw } from "lucide-react";

const ENTITY_TYPES = ["CA", "CS", "consultant", "broker", "individual", "firm", "other"];
const PRODUCTS = ["working_capital","term_loan","lap","invoice_discounting","revenue_based","structured","personal_loan"];

const empty = () => ({
  name: "", entity_name: "", entity_type: "CA",
  contact_email: "", contact_phone: "", city: "", state: "",
  pan: "", gstin: "",
  products: [], commission_pct: 1.0, commission_flat_inr: 0,
  bank_account_number: "", bank_ifsc: "",
  agreement_signed_on: "",
  channel_manager_email: "",
  notes: "", active: true, create_login: true,
});

export default function AdminChannelPartners() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/channel-partners", { params: { active_only: activeOnly } });
      setRows(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [activeOnly]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase(); if (!s) return rows;
    return rows.filter((r) =>
      [r.name, r.entity_name, r.contact_email, r.cp_code, r.city, r.state, (r.products || []).join(",")]
        .filter(Boolean).join(" ").toLowerCase().includes(s)
    );
  }, [rows, q]);

  const save = async () => {
    if (!draft.name || !draft.contact_email) { toast.error("Name + email required"); return; }
    setBusy(true);
    try {
      const body = { ...draft,
        commission_pct: Number(draft.commission_pct) || 0,
        commission_flat_inr: Number(draft.commission_flat_inr) || 0,
      };
      // Coerce empty strings on optional email/nullable fields to null so
      // pydantic EmailStr doesn't reject them with 422.
      ["channel_manager_email", "entity_name", "contact_phone", "pan", "gstin",
       "city", "state", "bank_account_number", "bank_ifsc", "agreement_signed_on",
       "notes"].forEach((k) => {
         if (body[k] === "") body[k] = null;
       });
      if (draft.partner_id) {
        const { create_login, partner_id, cp_code, user_id, ...patchBody } = body;
        void create_login; void partner_id; void cp_code; void user_id;
        await api.patch(`/admin/channel-partners/${draft.partner_id}`, patchBody);
        toast.success("Channel partner updated");
      } else {
        const r = await api.post("/admin/channel-partners", body);
        toast.success(`Partner ${r.data.cp_code} created`);
      }
      setDraft(null); await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const deactivate = async (p) => {
    if (!window.confirm(`Deactivate ${p.name} (${p.cp_code})? Historical rows preserved.`)) return;
    try { await api.delete(`/admin/channel-partners/${p.partner_id}`); toast.success("Deactivated"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const recompute = async () => {
    try {
      const r = await api.post("/admin/channel-partners/recompute-commissions");
      toast.success(`Accrued ${r.data.added} new commissions`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell title="Channel Partners" subtitle="VenturaZ Money · Partner CRM">
      <Toaster richColors position="top-right" />
      <div className="space-y-4" data-testid="admin-channel-partners">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <Search size={14} className="text-ink-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, code…"
              className="flex-1 px-3 py-2 border border-navy/15 rounded text-sm" data-testid="cp-search" />
            <label className="text-[11px] font-mono text-ink-muted inline-flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} data-testid="cp-active-only" />
              Active only
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={recompute} className="text-[11px] font-heading text-ink hover:bg-navy/5 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1"
              data-testid="cp-recompute">
              <RefreshCw size={12} /> Recompute commissions
            </button>
            <Button variant="gold" onClick={() => setDraft(empty())} testId="cp-new">
              <Plus size={12} className="inline mr-1" />New partner
            </Button>
          </div>
        </div>

        {loading ? <div className="text-sm italic text-ink-muted">Loading…</div>
        : filtered.length === 0 ? <div className="text-sm italic text-ink-muted border border-navy/10 rounded p-6 text-center" data-testid="cp-empty">No channel partners yet.</div>
        : (
          <div className="border border-navy/10 rounded bg-white/60 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-navy/[0.04] text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                <tr>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Name / Entity</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Contact</th>
                  <th className="text-left px-3 py-2">City</th>
                  <th className="text-left px-3 py-2">Commission</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy/[0.06]">
                {filtered.map((p) => (
                  <tr key={p.partner_id} className="hover:bg-navy/[0.02]" data-testid={`cp-row-${p.partner_id}`}>
                    <td className="px-3 py-2 font-mono text-[10px] text-navy">{p.cp_code}</td>
                    <td className="px-3 py-2">
                      <div className="font-heading text-ink">{p.name}</div>
                      <div className="text-[10px] font-mono text-ink-muted">{p.entity_name || "—"}</div>
                    </td>
                    <td className="px-3 py-2">{p.entity_type}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-ink-muted">
                      {p.contact_email}<br />{p.contact_phone || "—"}
                    </td>
                    <td className="px-3 py-2 text-[10px]">{p.city || "—"}{p.state ? `, ${p.state}` : ""}</td>
                    <td className="px-3 py-2 text-[10px] font-mono">
                      {p.commission_pct || 0}% + ₹{Number(p.commission_flat_inr || 0).toLocaleString("en-IN")}
                    </td>
                    <td className="px-3 py-2">{p.active ? <Pill tone="success">Active</Pill> : <Pill tone="muted">Inactive</Pill>}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setDraft({ ...p })} className="text-navy hover:underline text-[10px]" data-testid={`cp-edit-${p.partner_id}`}>Edit</button>
                      {p.contact_email && (
                        <button onClick={async () => {
                          try { await api.post(`/admin/channel-partners/${p.partner_id}/send-reset-link`); toast.success("Reset link sent"); }
                          catch (e) { toast.error(formatApiError(e)); }
                        }} className="ml-2 text-navy hover:underline text-[10px]" data-testid={`cp-reset-${p.partner_id}`}>Send reset link</button>
                      )}
                      {p.active && <button onClick={() => deactivate(p)} className="ml-2 text-rose-700 hover:underline text-[10px]" data-testid={`cp-deactivate-${p.partner_id}`}>Deactivate</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {draft && <PartnerEditor draft={draft} setDraft={setDraft} onSave={save} onCancel={() => setDraft(null)} busy={busy} />}
      </div>
    </DashboardShell>
  );
}

function PartnerEditor({ draft, setDraft, onSave, onCancel, busy }) {
  const toggleProduct = (p) => {
    const set = new Set(draft.products || []);
    if (set.has(p)) set.delete(p); else set.add(p);
    setDraft({ ...draft, products: Array.from(set) });
  };
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 flex items-start justify-center overflow-y-auto p-4" onClick={onCancel}>
      <div className="bg-white rounded max-w-3xl w-full my-6 shadow-lg border border-navy/15" onClick={(e) => e.stopPropagation()} data-testid="cp-editor">
        <div className="px-4 py-3 border-b border-navy/[0.08] flex items-center justify-between">
          <div>
            <div className="overline">{draft.partner_id ? "Edit partner" : "New channel partner"}</div>
            <div className="font-heading text-lg">{draft.name || "—"} {draft.cp_code && <span className="text-[10px] font-mono text-navy">· {draft.cp_code}</span>}</div>
          </div>
          <button onClick={onCancel}><X size={16} className="text-ink-muted" /></button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ["name", "Partner name *"],
            ["entity_name", "Entity / firm name"],
            ["contact_email", "Contact email *"],
            ["contact_phone", "Contact phone"],
            ["pan", "PAN"],
            ["gstin", "GSTIN"],
            ["city", "City (3-letter code preferred, e.g. DEL)"],
            ["state", "State"],
            ["channel_manager_email", "Internal channel manager email"],
            ["bank_account_number", "Bank A/C"],
            ["bank_ifsc", "IFSC"],
            ["agreement_signed_on", "Agreement signed on"],
          ].map(([k, label]) => (
            <F key={k} label={label}>
              <input value={draft[k] || ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                className="fld" data-testid={`cp-${k}`} />
            </F>
          ))}
          <F label="Entity type">
            <select value={draft.entity_type} onChange={(e) => setDraft({ ...draft, entity_type: e.target.value })} className="fld bg-white" data-testid="cp-entity_type">
              {ENTITY_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </F>
          <F label="Commission % of disbursed">
            <input type="number" step="0.1" min="0" max="25" value={draft.commission_pct} onChange={(e) => setDraft({ ...draft, commission_pct: e.target.value })}
              className="fld" data-testid="cp-commission_pct" />
          </F>
          <F label="Flat commission ₹ (added on top)">
            <input type="number" min="0" value={draft.commission_flat_inr} onChange={(e) => setDraft({ ...draft, commission_flat_inr: e.target.value })}
              className="fld" data-testid="cp-commission_flat_inr" />
          </F>
          <div className="md:col-span-2">
            <F label="Products">
              <div className="flex flex-wrap gap-1 mt-1">
                {PRODUCTS.map((p) => {
                  const on = (draft.products || []).includes(p);
                  return (
                    <button key={p} type="button" onClick={() => toggleProduct(p)} data-testid={`cp-product-${p}`}
                      className={`text-[10px] font-mono px-2 py-1 rounded border ${on ? "bg-gold/25 border-gold/50 text-[#8A6600]" : "bg-navy/[0.03] border-navy/15 text-ink-muted hover:bg-navy/10"}`}>
                      {p}
                    </button>
                  );
                })}
              </div>
            </F>
          </div>
          <div className="md:col-span-2">
            <F label="Internal notes"><textarea rows={2} value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="fld" data-testid="cp-notes" /></F>
          </div>
          <div className="md:col-span-2 flex items-center gap-4 flex-wrap">
            <label className="text-xs inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} data-testid="cp-active" />
              Active
            </label>
            {!draft.partner_id && (
              <label className="text-xs inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!draft.create_login} onChange={(e) => setDraft({ ...draft, create_login: e.target.checked })} data-testid="cp-create_login" />
                Auto-create partner login (they set password on first sign-in)
              </label>
            )}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-navy/[0.08] flex items-center justify-end gap-2">
          <button onClick={onCancel} className="text-xs text-ink-muted hover:text-ink px-3 py-2" data-testid="cp-cancel">Cancel</button>
          <Button variant="gold" onClick={onSave} disabled={busy} testId="cp-save">
            <Save size={12} className="inline mr-1" />{busy ? "Saving…" : (draft.partner_id ? "Update partner" : "Create partner")}
          </Button>
        </div>
      </div>
      <style>{`.fld{width:100%;margin-top:2px;padding:8px 12px;border:1px solid rgba(15,29,64,0.15);border-radius:4px;font-size:14px}`}</style>
    </div>
  );
}
function F({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{label}</label>
      {children}
    </div>
  );
}
